import React from 'react'
import {
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from 'react-icons/fa'

/**
 * Pagination component for table or list navigation.
 *
 * @param {Object} props - Component properties.
 * @param {number} [props.activePage=1] - Currently selected page number (1-indexed).
 * @param {number} [props.pages] - Total number of pages.
 * @param {number} [props.totalPages] - Total number of pages (alias for `pages`).
 * @param {(page: number) => void} [props.onActivePageChange] - Callback triggered when active page changes.
 * @param {boolean} [props.doubleArrows=true] - Whether to show first and last page navigation buttons.
 * @param {boolean} [props.arrows=true] - Whether to show previous and next page navigation buttons.
 * @param {'left' | 'center' | 'right' | 'start' | 'end'} [props.align='left'] - Horizontal alignment of pagination controls.
 * @param {'sm' | 'lg'} [props.size] - Size variant for pagination buttons.
 * @param {boolean} [props.dots=true] - Whether to display ellipsis ('...') for hidden page numbers.
 * @param {number} [props.limit=5] - Maximum number of page numbers visible at once.
 * @param {boolean} [props.disabled=false] - Disables interaction with pagination controls.
 * @param {React.ReactNode} [props.firstButton] - Custom label/icon for the first page button.
 * @param {React.ReactNode} [props.lastButton] - Custom label/icon for the last page button.
 * @param {React.ReactNode} [props.previousButton] - Custom label/icon for the previous page button.
 * @param {React.ReactNode} [props.nextButton] - Custom label/icon for the next page button.
 * @param {string} [props.className=''] - Additional CSS classes for the container element.
 * @returns {JSX.Element | null} Pagination control list or null if total pages <= 0.
 */
const Pagination = ({
  activePage = 1,
  pages,
  totalPages,
  onActivePageChange,
  doubleArrows = true,
  arrows = true,
  align = 'left',
  size,
  dots = true,
  limit = 5,
  disabled = false,
  firstButton,
  lastButton,
  previousButton,
  nextButton,
  className = '',
  ...restProps
}) => {
  const pageCount = pages !== undefined ? pages : totalPages || 0

  if (!pageCount || pageCount <= 0) {
    return null
  }

  const currentPage = Math.max(1, Math.min(activePage, pageCount))

  const handlePageClick = (page) => {
    if (disabled || page < 1 || page > pageCount || page === currentPage) return
    if (onActivePageChange) {
      onActivePageChange(page)
    }
  }

  // Alignment classes
  let justifyClass = 'justify-start'
  if (align === 'center') {
    justifyClass = 'justify-center'
  } else if (align === 'right' || align === 'end') {
    justifyClass = 'justify-end'
  }

  // Size classes
  let itemSizeClass = 'h-8 min-w-[32px] px-2.5 text-xs'
  let iconSizeClass = 'text-xs'
  if (size === 'sm') {
    itemSizeClass = 'h-7 min-w-[28px] px-2 text-[11px]'
    iconSizeClass = 'text-[10px]'
  } else if (size === 'lg') {
    itemSizeClass = 'h-10 min-w-[40px] px-3.5 text-sm'
    iconSizeClass = 'text-sm'
  }

  // Calculate visible page range
  const getPageNumbers = () => {
    const pageNumbers = []
    const maxVisible = Math.max(1, limit)

    if (!dots || pageCount <= maxVisible + 2) {
      for (let i = 1; i <= pageCount; i++) {
        pageNumbers.push(i)
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let endPage = Math.min(pageCount, startPage + maxVisible - 1)

      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1)
      }

      if (startPage > 1) {
        pageNumbers.push(1)
        if (startPage > 2) {
          pageNumbers.push('...')
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i)
      }

      if (endPage < pageCount) {
        if (endPage < pageCount - 1) {
          pageNumbers.push('...')
        }
        pageNumbers.push(pageCount)
      }
    }

    return pageNumbers
  }

  const pageNumbers = getPageNumbers()

  return (
    <ul
      {...restProps}
      className={`flex items-center gap-1 list-none p-0 m-0 select-none ${justifyClass} ${className}`}
    >
      {/* First Page (Double Left Arrow) */}
      {doubleArrows && (
        <li>
          <button
            type="button"
            onClick={() => handlePageClick(1)}
            disabled={disabled || currentPage === 1}
            aria-label="First Page"
            className={`flex items-center justify-center rounded-lg border border-aimhi-border bg-white text-aimhi-textlight-neutral font-medium transition-colors ${itemSizeClass} ${disabled || currentPage === 1
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-gray-100 hover:text-aimhi-textlight-neutral cursor-pointer'
              }`}
          >
            {firstButton || <FaAngleDoubleLeft className={iconSizeClass} />}
          </button>
        </li>
      )}

      {/* Previous Page (Single Left Arrow) */}
      {arrows && (
        <li>
          <button
            type="button"
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={disabled || currentPage === 1}
            aria-label="Previous Page"
            className={`flex items-center justify-center rounded-lg border border-aimhi-border bg-white text-aimhi-textlight-neutral font-medium transition-colors ${itemSizeClass} ${disabled || currentPage === 1
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-gray-100 hover:text-aimhi-textlight-neutral cursor-pointer'
              }`}
          >
            {previousButton || <FaChevronLeft className={iconSizeClass} />}
          </button>
        </li>
      )}

      {/* Page Numbers */}
      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            <li key={`ellipsis-${index}`}>
              <span
                className={`flex items-center justify-center text-aimhi-textlight-muted font-medium ${itemSizeClass}`}
              >
                ...
              </span>
            </li>
          )
        }

        const isActive = page === currentPage

        return (
          <li key={page}>
            <button
              type="button"
              onClick={() => handlePageClick(page)}
              disabled={disabled}
              className={`flex items-center justify-center rounded-lg border font-medium transition-colors ${itemSizeClass} ${disabled
                ? 'opacity-40 cursor-not-allowed border-aimhi-border bg-white text-aimhi-textlight-neutral'
                : isActive
                  ? 'bg-aimhi-textlight-neutral text-white border-aimhi-textlight-neutral font-semibold shadow-xs'
                  : 'bg-white text-aimhi-textlight-neutral border-aimhi-border hover:bg-gray-100 cursor-pointer'
                }`}
            >
              {page}
            </button>
          </li>
        )
      })}

      {/* Next Page (Single Right Arrow) */}
      {arrows && (
        <li>
          <button
            type="button"
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={disabled || currentPage === pageCount}
            aria-label="Next Page"
            className={`flex items-center justify-center rounded-lg border border-aimhi-border bg-white text-aimhi-textlight-neutral font-medium transition-colors ${itemSizeClass} ${disabled || currentPage === pageCount
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-gray-100 hover:text-aimhi-textlight-neutral cursor-pointer'
              }`}
          >
            {nextButton || <FaChevronRight className={iconSizeClass} />}
          </button>
        </li>
      )}

      {/* Last Page (Double Right Arrow) */}
      {doubleArrows && (
        <li>
          <button
            type="button"
            onClick={() => handlePageClick(pageCount)}
            disabled={disabled || currentPage === pageCount}
            aria-label="Last Page"
            className={`flex items-center justify-center rounded-lg border border-aimhi-border bg-white text-aimhi-textlight-neutral font-medium transition-colors ${itemSizeClass} ${disabled || currentPage === pageCount
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-gray-100 hover:text-aimhi-textlight-neutral cursor-pointer'
              }`}
          >
            {lastButton || <FaAngleDoubleRight className={iconSizeClass} />}
          </button>
        </li>
      )}
    </ul>
  )
}

export default Pagination
